/**
 * Persistência local (localStorage) do formulário de criação da Proposta de Projeto (PCM).
 *
 * Objetivo: nunca perder o que o usuário preencheu quando o cadastro falha (erro de API,
 * queda de rede, refresh acidental). O rascunho é salvo por usuário e por tipo de PCM,
 * atualizado com debounce enquanto o formulário está em modo «criar» e removido apenas
 * após o cadastro ser confirmado pela API.
 *
 * Este módulo não conhece a IA: quem restaura o rascunho deve reprocessar o ownership
 * dos campos (initializeManualOwnership) para que a IA não sobrescreva o que voltou.
 */

export const PCM_DRAFT_VERSION = 1
export const PCM_DRAFT_MAX_AGE_DIAS = 30

const PREFIXO_CHAVE = 'thalamus-projetos:rascunho-pcm'

/** Campos que nunca fazem sentido em um rascunho de criação. */
const CAMPOS_IGNORADOS = new Set([
    'id',
    'codigo',
    'pcm_codigo',
    'anexos',
    'comite_aprovacao',
    'pedidos_edicao',
    'id_associacao',
    'projeto_id',
    'programa_id',
    'planoAcao_id',
    'created_at',
    'updated_at',
    'deleted_at',
])

/** Campos considerados para decidir se há conteúdo digno de ser guardado. */
const CAMPOS_CONTEUDO = [
    'nome',
    'objetivo_geral',
    'descricao_problema',
    'escopo',
    'ganho_operacional_esperado',
    'custo_justificativa',
    'possivel_solucao',
    'setor_id',
    'setorDemanda_id',
    'gerentePCM_id',
    'dtInicio',
    'dtEstimadaFinal',
    'finalidade',
    'area',
    'nivel',
]

const CAMPOS_RESULTADO_CONTEUDO = ['nome', 'problema', 'descricao', 'evidencias', 'custo', 'dt_prevista_conclusao']

function temValor(valor) {
    if (valor === null || valor === undefined) return false
    if (typeof valor === 'string') return valor.trim() !== ''
    if (Array.isArray(valor)) return valor.length > 0
    return true
}

function storageDisponivel() {
    try {
        return typeof window !== 'undefined' && !!window.localStorage
    } catch {
        return false
    }
}

export function chaveRascunhoPcm(tipo, usuarioId) {
    return `${PREFIXO_CHAVE}:v${PCM_DRAFT_VERSION}:${tipo || 'Projeto'}:${usuarioId ?? 'anon'}`
}

/**
 * Remove chaves não serializáveis/irrelevantes e clona o PCM de forma segura.
 * Datas viram string (slice 10) e objetos File são descartados.
 */
export function serializarPcmParaRascunho(pcm, extras = {}) {
    if (!pcm || typeof pcm !== 'object') return null

    const dados = {}
    Object.keys(pcm).forEach((chave) => {
        if (CAMPOS_IGNORADOS.has(chave)) return
        const valor = pcm[chave]
        if (typeof valor === 'function') return
        if (typeof File !== 'undefined' && valor instanceof File) return
        dados[chave] = valor
    })

    if (Array.isArray(extras.objetivos_estrategicos)) {
        dados.objetivos_estrategicos = extras.objetivos_estrategicos
    }
    if (Array.isArray(extras.impacto_viabilidade)) {
        dados.impacto_viabilidade = extras.impacto_viabilidade
    }

    try {
        return JSON.parse(JSON.stringify(dados))
    } catch {
        return null
    }
}

/** Indica se o rascunho tem algo que valha a pena guardar/restaurar. */
export function rascunhoTemConteudo(dados) {
    if (!dados || typeof dados !== 'object') return false

    if (CAMPOS_CONTEUDO.some((campo) => temValor(dados[campo]))) return true

    if (Array.isArray(dados.resultados) && dados.resultados.some((r) =>
        r && CAMPOS_RESULTADO_CONTEUDO.some((campo) => temValor(r[campo])),
    )) return true

    if (Array.isArray(dados.setores_envolvidos) && dados.setores_envolvidos.length) return true

    if (Array.isArray(dados.objetivos_estrategicos) && dados.objetivos_estrategicos.some((o) =>
        o?.ativo === 1 || o?.ativo === true || o?.ativo === '1',
    )) return true

    if (Array.isArray(dados.impacto_viabilidade) && dados.impacto_viabilidade.some((i) =>
        typeof i?.concordo === 'boolean' || temValor(i?.justificativa),
    )) return true

    return false
}

/**
 * Salva o rascunho. Retorna `true` quando gravou.
 * Falhas (quota excedida, storage bloqueado) são silenciosas: o rascunho é uma rede de segurança,
 * nunca deve quebrar o fluxo principal.
 */
export function salvarRascunhoPcm(chave, pcm, extras = {}, meta = {}) {
    if (!storageDisponivel() || !chave) return false

    const dados = serializarPcmParaRascunho(pcm, extras)
    if (!dados || !rascunhoTemConteudo(dados)) return false

    const envelope = {
        versao: PCM_DRAFT_VERSION,
        salvoEm: new Date().toISOString(),
        motivo: meta.motivo || 'autosave',
        anexosPendentes: Number(meta.anexosPendentes || 0),
        dados,
    }

    try {
        window.localStorage.setItem(chave, JSON.stringify(envelope))
        return true
    } catch (error) {
        if (process.env.NODE_ENV !== 'production') console.warn('Não foi possível salvar o rascunho do PCM', error)
        return false
    }
}

/**
 * Carrega o rascunho, validando versão, idade e conteúdo. Retorna `null` quando não há nada útil.
 */
export function carregarRascunhoPcm(chave) {
    if (!storageDisponivel() || !chave) return null

    let bruto = null
    try {
        bruto = window.localStorage.getItem(chave)
    } catch {
        return null
    }
    if (!bruto) return null

    let envelope = null
    try {
        envelope = JSON.parse(bruto)
    } catch {
        limparRascunhoPcm(chave)
        return null
    }

    if (!envelope || envelope.versao !== PCM_DRAFT_VERSION || !envelope.dados) {
        limparRascunhoPcm(chave)
        return null
    }

    const salvoEm = Date.parse(envelope.salvoEm)
    if (Number.isFinite(salvoEm)) {
        const idadeDias = (Date.now() - salvoEm) / (1000 * 60 * 60 * 24)
        if (idadeDias > PCM_DRAFT_MAX_AGE_DIAS) {
            limparRascunhoPcm(chave)
            return null
        }
    }

    if (!rascunhoTemConteudo(envelope.dados)) {
        limparRascunhoPcm(chave)
        return null
    }

    return envelope
}

export function limparRascunhoPcm(chave) {
    if (!storageDisponivel() || !chave) return
    try {
        window.localStorage.removeItem(chave)
    } catch {
        /* ignorar */
    }
}

/**
 * Reconcilia uma lista salva (objetivos/impactos) com o catálogo atual do tenant:
 * mantém os itens do catálogo e traz do rascunho apenas as escolhas do usuário.
 */
export function mesclarListaComCatalogo(catalogo, salvos, idCampo, camposUsuario) {
    const base = Array.isArray(catalogo) && catalogo.length ? catalogo : (Array.isArray(salvos) ? salvos : [])
    const porId = new Map()
    ;(Array.isArray(salvos) ? salvos : []).forEach((item) => {
        const id = item?.[idCampo]
        if (id !== undefined && id !== null) porId.set(String(id), item)
    })

    return base.map((item) => {
        const salvo = porId.get(String(item?.[idCampo]))
        if (!salvo) return { ...item }
        const mesclado = { ...item }
        camposUsuario.forEach((campo) => {
            if (campo in salvo) mesclado[campo] = salvo[campo]
        })
        return mesclado
    })
}

/**
 * Aplica o rascunho no objeto PCM **in place** (mesma referência usada pelo pai e pela IA).
 * Retorna a lista de campos restaurados.
 */
export function aplicarRascunhoNoPcm(pcm, dados, catalogos = {}) {
    if (!pcm || !dados || typeof dados !== 'object') return []

    const restaurados = []
    Object.keys(dados).forEach((chave) => {
        if (CAMPOS_IGNORADOS.has(chave)) return
        if (chave === 'tipo') return // o tipo é definido pela rota/tela, não pelo rascunho
        if (chave === 'objetivos_estrategicos' || chave === 'impacto_viabilidade') return
        pcm[chave] = dados[chave]
        restaurados.push(chave)
    })

    if (Array.isArray(dados.objetivos_estrategicos)) {
        pcm.objetivos_estrategicos = mesclarListaComCatalogo(
            catalogos.objetivos,
            dados.objetivos_estrategicos,
            'id',
            ['ativo', 'justificativa'],
        )
        restaurados.push('objetivos_estrategicos')
    }

    if (Array.isArray(dados.impacto_viabilidade)) {
        pcm.impacto_viabilidade = mesclarListaComCatalogo(
            catalogos.impactos,
            dados.impacto_viabilidade,
            'impactoViabilidade_id',
            ['concordo', 'justificativa', 'impact_state', 'impact_reason', 'impact_confidence', 'impact_requires_confirmation'],
        )
        restaurados.push('impacto_viabilidade')
    }

    return restaurados
}
